import { useCallback, useState } from "react";
import { MSOData, ObjectStringKeys, PriorCommitments } from "../types";
import { useInitialData } from "./useInitialData";

export function useOffenseData() {
  const { codesData } = useInitialData();
  const [data, setData] = useState<MSOData>();
  const [isCurrentFelonyDangerous, setIsCurrentFelonyDangerous] =
    useState<boolean>(false);

  // former getTheData function
  const updateData = useCallback(
    (offenseCode: string) => {
      if (
        !(codesData.length > 0)
      ) {
        return undefined;
      }

      // Find the relevant row in Conditions.csv
      let offenseData: MSOData = {
        ...codesData.filter(
          (row: MSOData) => row["MO Offense Code"] === offenseCode
        )[0],
      };

      const felonyClassCol = offenseData["Felony Class"];
      let offenseTypeCol = offenseData["Parole Offense Type"];

      // If offense type is missing, assuming nonviolent
      if (offenseTypeCol === null) {
        offenseTypeCol = "NVI";
      }

      const isDangerousFelony = offenseData["Dangerous Felony"] == "Y";
      setIsCurrentFelonyDangerous(isDangerousFelony);
      const isMPT = offenseData["MPT"] == "Y";
      offenseData.isMPT = isMPT;

      offenseData["Sentencing Range"] = sentencingRange(felonyClassCol[0]);
      offenseData["Felony Class Expanded"] = felonyClass(felonyClassCol[0]);
      offenseData["Parole Offense Type Expanded"] = offenseType(offenseTypeCol);

      // Used for Timeline
      offenseData["Minimum Percentage"] = minimumPercentage(
        offenseTypeCol,
        felonyClassCol,
        isDangerousFelony
      );
      offenseData["Maximum Percentage"] = maximumPercentage(
        Number(offenseData["Sentencing Range"][0]),
        isDangerousFelony,
        "0"
      );

      // Used for Relevant Rules
      // Min
      offenseData["Minimum Prison Time Determined By"] =
        minimumDeterminedBy(isDangerousFelony);
      offenseData["Minimum Parole Eligibility Text"] = minimumParoleEligibility(
        offenseTypeCol,
        felonyClassCol,
        offenseData["Minimum Percentage"],
        isDangerousFelony
      );
      offenseData["Minimum Prison Time Warning Text"] =
        minimumPrisonTimeWarning(isMPT);
      // Max
      offenseData["Maximum Prison Time Determined By Text"] =
        maximumDeterminedBy(isDangerousFelony);
      offenseData["Maximum Prison Time Served Text"] = maximumPrisonTime(
        Number(offenseData["Sentencing Range"][0]),
        isDangerousFelony
      );

      // Used for Historical Text
      offenseData["Historical Text"] = historicalText(
        isDangerousFelony,
        offenseTypeCol,
        felonyClassCol,
        isMPT
      );

      setData(offenseData);
      return {
        chargeCode: offenseCode,
        defaultSentence: parseInt(offenseData["Sentencing Range"][0]),
        minPercentage: offenseData["Minimum Percentage"],
        maxPercentage: offenseData["Maximum Percentage"],
        offenseType: offenseData["Parole Offense Type"],
        felonyClass: offenseData["Felony Class"],
      };
    },
    [codesData]
  );

  const updateSentence = useCallback(
    (sentenceImposed: number, priorCommitments: PriorCommitments) => {
      const newMaxPercentage = maximumPercentage(
        sentenceImposed,
        isCurrentFelonyDangerous,
        priorCommitments
      );
      const newMaxPrisonTime = maximumPrisonTime(
        sentenceImposed,
        isCurrentFelonyDangerous
      );

      setData((data) => {
        if (data) {
          return {
            ...data,
            "Maximum Percentage": newMaxPercentage,
            "Maximum Prison Time Served Text": newMaxPrisonTime,
          };
        }
        return undefined;
      });

      return { maxPercentage: newMaxPercentage };
    },
    [isCurrentFelonyDangerous]
  );

  const updatePriorCommitments = useCallback(
    (
      sentenceImposed: number,
      priorCommitments: PriorCommitments,
      offenseType: string,
      felonyClass: string,
      isMPT: boolean
    ) => {
      const newMaxPercentage = maximumPercentage(
        sentenceImposed,
        isCurrentFelonyDangerous,
        priorCommitments
      );
      const newMinPercentage = minimumPercentage(
        offenseType,
        felonyClass,
        isCurrentFelonyDangerous,
        priorCommitments
      );
      const newMinimumDeterminedBy = minimumDeterminedBy(
        isCurrentFelonyDangerous,
        priorCommitments
      );
      const newHistoricalText = historicalText(
        isCurrentFelonyDangerous,
        offenseType,
        felonyClass,
        isMPT,
        priorCommitments
      );

      setData((data) => {
        if (data) {
          return {
            ...data,
            "Maximum Percentage": newMaxPercentage,
            "Minimum Percentage": newMinPercentage,
            "Minimum Prison Time Determined By": newMinimumDeterminedBy,
            "Historical Text": newHistoricalText,
          };
        }
        return undefined;
      });

      return { maxPercentage: newMaxPercentage };
    },
    [isCurrentFelonyDangerous]
  );

  return {
    data,
    updateData,
    updateSentence,
    updatePriorCommitments,
    isCurrentFelonyDangerous,
  };
}

function felonyClass(offenseCode: string) {
  if (offenseCode === "A") {
    return "A: 10 to 30 years";
  } else if (offenseCode === "B") {
    return "B: 5 to 15 years";
  } else if (offenseCode === "C") {
    return "C: 3 to 10 years";
  } else if (offenseCode === "D") {
    return "D: 1 to 7 years";
  } else if (offenseCode === "E") {
    return "E: 1 to 4 years";
  } else if (offenseCode === "U") {
    return "U: Unclassified";
  }
  return "";
}

function sentencingRange(felonyClass: string): [string, string] {
  if (felonyClass === "A") {
    return ["10", "30"];
  } else if (felonyClass === "B") {
    return ["5", "15"];
  } else if (felonyClass === "C") {
    return ["3", "10"];
  } else if (felonyClass === "D") {
    return ["1", "7"];
  } else if (felonyClass === "E") {
    return ["1", "4"];
  } else if (felonyClass === "U") {
    return ["1", "100"];
  }
  return ["??", "??"];
}

function offenseType(offenseType: string) {
  if (offenseType === "NVI") {
    return "Nonviolent";
  } else if (offenseType === "VIO") {
    return "Violent";
  } else if (offenseType === "DRG") {
    return "Drug";
  } else if (offenseType === "DWI") {
    return "DWI";
  } else if (offenseType === "SEX") {
    return "Sex offense";
  }
  return "";
}

export function minimumPercentage(
  offenseType: string,
  felonyClass: string,
  isDangerousFelony: boolean,
  priorCommitments?: PriorCommitments
): number {
  const min_path_map: ObjectStringKeys = {
    NVI: { A: 0.25, B: 0.25, C: 0.25, D: 0.15, E: 0.15 },
    DRG: { A: 0.2, B: 0.2, C: 0.2, D: 0.15, E: 0.15 },
    DWI: { A: 0.25, B: 0.25, C: 0.25 },
  };

  const priorCommitmentsMap: {
    [priorCommitments in PriorCommitments]: number;
  } = {
    "0": 0,
    "1": 0.4,
    "2": 0.5,
    "3+": 0.8,
  };

  if (isDangerousFelony) {
    return 0.85;
  } else if (priorCommitments && priorCommitments !== "0") {
    return priorCommitmentsMap[priorCommitments];
  } else if (offenseType === "VIO" || offenseType === "SEX") {
    return 0.33;
  } else {
    return min_path_map[offenseType][felonyClass] || 0;
  }
}

function conditionalReleaseTerm(yearsSentenced: number) {
  if (yearsSentenced * 12 <= 108) {
    return (yearsSentenced * 12) / 3;
  } else if (yearsSentenced * 12 <= 180) {
    return 36;
  } else {
    return 60;
  }
}

export function maximumPercentage(
  yearsSentenced: number,
  isDangerousFelony: boolean,
  priorCommitments: PriorCommitments
): number {
  if (isDangerousFelony || priorCommitments === "3+") {
    return 1;
  } else {
    const CRT = conditionalReleaseTerm(yearsSentenced);
    return (yearsSentenced * 12 - CRT) / (yearsSentenced * 12);
  }
}

function minimumDeterminedBy(
  isDangerousFelony: boolean,
  priorCommitments?: string
) {
  if (isDangerousFelony)
    return {
      label: "Dangerous Felony Statute",
      link: "https://revisor.mo.gov/main/OneSection.aspx?section=556.061",
    };
  if (priorCommitments && priorCommitments !== "0") {
    let text =
      "If the offender has one previous prison commitment to the Department of Corrections for a felony offense listed under § 558.019, the minimum prison term which the offender must serve shall be 40 percent of his or her sentence or until the offender attains seventy years of age, and has served at least 30 percent of the sentence imposed, whichever occurs first.";
    if (priorCommitments === "2") {
      text =
        "If the offender has two previous prison commitments to the Department of Corrections for a felony offense listed under § 558.019, the minimum prison term which the offender must serve shall be 50 percent of his or her sentence or until the offender attains seventy years of age, and has served at least 40 percent of the sentence imposed, whichever occurs first.";
    }
    if (priorCommitments === "3+") {
      text =
        "If the offender has three or more previous prison commitments to the Department of Corrections for a felony offense listed under § 558.019, the minimum prison term which the offender must serve shall be 80 percent of his or her sentence or until the offender attains seventy years of age, and has served at least 40 percent of the sentence imposed, whichever occurs first.";
    }
    return {
      label: "Statute RSMo § 558.019",
      link: "https://revisor.mo.gov/main/OneSection.aspx?section=558.019&bid=47815&hl",
      text,
    };
  }
  return {
    label: "Parole Board Regulatory Minimums",
    link: "https://doc.mo.gov/sites/doc/files/2018-01/Blue-Book.pdf",
  };
}

function minimumParoleEligibility(
  offenseTypeCol: string,
  felonyClassCol: string,
  minimumPercentage: number,
  isDangerousFelony: boolean
) {
  if (isDangerousFelony) {
    return "Any offender who has been found guilty of a dangerous felony shall be required to serve a minimum prison term of 85 percent of the sentence imposed by the court or until the offender attains seventy years of age, and has served at least forty percent of the sentence imposed, whichever occurs first.";
  } else if (offenseTypeCol === "VIO" || offenseTypeCol === "SEX") {
    return "People convicted of sexual or child abuse and violent offenses (all classes of offenses) may not be eligible for parole until 33 percent of the maximum sentence bas been served; except when the statute would require more time to be served.";
  } else if (minimumPercentage !== undefined) {
    return (
      "People convicted of " +
      offenseType(offenseTypeCol) +
      " class " +
      felonyClassCol +
      " offenses may not be eligible for parole until " +
      minimumPercentage * 100 +
      " percent of the maximum sentence has been served; except where statute would require more time to be served."
    );
  } else {
    return "";
  }
}

function minimumPrisonTimeWarning(isMPT: boolean) {
  if (isMPT) {
    return "If the person has been previously committed to the Missouri Department of Corrections for offenses listed in section 558.019, RSMo, this offense may trigger a minimum prison term. One prior prison commitment for a listed offense would require 40 percent of this sentence to be served; two prior prison commitments for listed offenses would require 50 percent of the sentence to be served; and three or more prior prison commitments would require 80 percent to be served.";
  }
  return "";
}

function maximumDeterminedBy(isDangerousFelony: boolean) {
  let determinedBy: string;
  if (isDangerousFelony) {
    determinedBy = "Court Imposed Max Sentence";
  } else {
    determinedBy = "conditional release statute";
  }
  return determinedBy;
}

function maximumPrisonTime(
  sentenceImposed: number,
  isDangerousFelony: boolean
) {
  if (isDangerousFelony) {
    return "People convicted of offenses classified as “Dangerous Felonies” may serve up to 100 percent of their sentence in prison.";
  } else if (sentenceImposed < 9) {
    return "The conditional release term shall be one-third (33 percent) for terms of nine years or less.";
  } else if (sentenceImposed >= 9 && sentenceImposed <= 15) {
    return "The conditional release term shall be three years for terms between nine and fifteen years.";
  } else if (sentenceImposed > 15) {
    return "The conditional release term shall be five years for terms more than fifteen years.";
  } else {
    return "";
  }
}

function historicalText(
  isDangerousFelony: boolean,
  offenseType: string,
  felonyClass: string,
  isMPT: boolean,
  priorCommitments?: PriorCommitments
) {
  let offenseTypeText = "";
  let prisonPercentText = "";

  if (isDangerousFelony) {
    offenseTypeText = "dangerous felonies";
    prisonPercentText = "85 percent";
  } else if (
    ["A", "B", "C"].includes(felonyClass) &&
    isMPT &&
    priorCommitments &&
    priorCommitments !== "0"
  ) {
    const priorCommitmentsTextMap = {
      "1": { text: "one", percent: "52%" },
      "2": { text: "two", percent: "58%" },
      "3+": { text: "three or more", percent: "83%" },
    };
    offenseTypeText = `an offense of this type with ${
      priorCommitmentsTextMap[priorCommitments].text
    } prior ${priorCommitments === "1" ? "commitment" : "commitments"}`;
    prisonPercentText = priorCommitmentsTextMap[priorCommitments].percent;
  } else if (
    ["A", "B", "C", "D", "E"].includes(felonyClass) &&
    offenseType === "VIO"
  ) {
    offenseTypeText = "Class A, B, C, D, or E violent offenses";
    prisonPercentText = "61 percent";
  } else if (offenseType === "SEX") {
    offenseTypeText = "Class A - E sex offenses";
    prisonPercentText = "75 percent";
  } else if (
    ["A", "B", "C"].includes(felonyClass) &&
    (offenseType === "NVI" || offenseType === "DWI")
  ) {
    offenseTypeText = "Class A, B, or C non-violent/DWI offenses";
    prisonPercentText = "42 percent";
  } else if (["A", "B", "C"].includes(felonyClass) && offenseType === "DRG") {
    offenseTypeText = "Class A, B, or C drug offenses";
    prisonPercentText = "30 percent";
  } else if (
    ["D", "E"].includes(felonyClass) &&
    (offenseType === "NVI" || offenseType === "DWI" || offenseType === "DRG")
  ) {
    offenseTypeText = "Class D or E non-violent/DWI/Drug offenses";
    prisonPercentText = "31 percent";
  } else {
    return "";
  }

  return `On average, people incarcerated in Missouri over the past 11 years for ${offenseTypeText} served ${prisonPercentText} of their sentence in prison.`;
}
